/**
 * Onboarding component. First-run: collect first name, locale, passcode,
 * and persist an empty encrypted vault to OPFS. Then transition to ready.
 */
import { store } from '../store.ts';
import { emptyVault } from '../types.ts';
import type { VaultOwner } from '../types.ts';
import { encryptVault } from '../chassis/crypto.ts';
import { saveEnvelope, saveMeta } from '../chassis/storage.ts';

const TPL = /* html */ `
<style>
:host {
  display: grid;
  place-items: center;
  inset: 0;
  position: fixed;
  background:
    radial-gradient(ellipse 70% 50% at 50% -10%, oklch(96% 0.020 80), transparent 65%),
    radial-gradient(ellipse 120% 80% at 50% 110%, oklch(82% 0.045 70 / 0.5), transparent 60%),
    var(--parchment);
  overflow-y: auto;
  padding: 28px 20px;
}
:host::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      to bottom,
      transparent 0 27px,
      oklch(60% 0.040 50 / 0.075) 27px 28px
    );
  opacity: 0.55;
}
section {
  position: relative;
  z-index: 2;
  max-width: 460px;
  width: 100%;
  display: grid;
  gap: 14px;
  padding: 30px 24px;
}
.top-rule, .bot-rule {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-dim);
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink);
}
.bot-rule {
  border-bottom: 0;
  border-top: 1px solid var(--ink);
  padding-top: 16px;
  padding-bottom: 0;
  margin-top: 22px;
}
.bot-rule .crimson { color: var(--indigo); font-weight: 700; }
.top-rule .center, .bot-rule .center {
  display: flex; align-items: center; justify-content: center; gap: 8px;
}
.top-rule .dot, .bot-rule .dot {
  width: 4px; height: 4px; background: var(--indigo);
  border-radius: 50%; display: inline-block;
}
.mark {
  font-family: var(--hindi);
  font-size: clamp(86px, 22vw, 132px);
  font-weight: 400;
  color: var(--indigo);
  line-height: 0.88;
  letter-spacing: -0.02em;
  text-align: center;
  margin-top: 6px;
}
.en {
  font-family: var(--display);
  font-style: italic;
  font-weight: 600;
  font-size: clamp(26px, 7vw, 34px);
  color: var(--ink);
  text-align: center;
  margin-top: -2px;
}
.en::before, .en::after {
  content: '·';
  color: var(--indigo);
  margin: 0 12px;
  opacity: 0.7;
}
.quote {
  font-family: var(--display);
  font-size: 17px;
  font-style: italic;
  color: var(--ink);
  font-weight: 500;
  margin: 16px 0 4px;
  line-height: 1.4;
  text-wrap: balance;
  text-align: center;
}
.attr {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-faint);
  text-align: center;
  margin-bottom: 12px;
}
.steps {
  display: grid;
  gap: 14px;
  margin-top: 8px;
}
.step {
  display: grid;
  gap: 6px;
}
.step-label {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-dim);
}
input, select {
  font-family: var(--body);
  font-size: 16px;
  padding: 13px 14px;
  background: oklch(96% 0.018 78);
  border: 1px solid var(--rule-strong);
  border-radius: 2px;
  color: var(--ink);
  width: 100%;
  transition: border-color 200ms cubic-bezier(0.32, 0.72, 0, 1);
}
input[type='password'] {
  font-family: var(--mono);
  letter-spacing: 0.32em;
  text-align: center;
}
input:focus, select:focus { border-color: var(--indigo); outline: none; }
.row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
button[type='submit'] {
  font-family: var(--mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.42em;
  padding: 18px;
  background: var(--indigo);
  color: var(--parchment);
  border-radius: 2px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  position: relative;
  overflow: hidden;
  margin-top: 12px;
}
button[type='submit']::before {
  content: '';
  position: absolute;
  inset: 1px;
  border: 1px solid oklch(94% 0.022 78 / 0.35);
  pointer-events: none;
}
button[type='submit']:hover { filter: brightness(1.08); }
.err {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--crimson);
  letter-spacing: 0.06em;
  min-height: 14px;
  margin-top: 8px;
  font-weight: 700;
}
.hint {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.2em;
  color: var(--ink-faint);
  text-transform: uppercase;
  text-align: center;
  margin-top: 4px;
}
</style>
<section>
  <div class="top-rule">
    <span>Vol. I</span>
    <span class="center">वसीयत<span class="dot"></span>The Family Vault<span class="dot"></span>Issue I</span>
    <span id="date"></span>
  </div>
  <div class="mark">वसीयत</div>
  <div class="en">Wasiyat</div>
  <div class="quote">Jo apke baad reh jaaye, woh aap nahi - woh aapki vasiyat hai.</div>
  <div class="attr">— Aditya, Founder</div>

  <form class="steps" id="form">
    <div class="step">
      <span class="step-label">Step 1 · First name</span>
      <input id="name" type="text" placeholder="e.g. Aditya" required autocomplete="given-name" />
    </div>
    <div class="row">
      <label class="step">
        <span class="step-label">Locale</span>
        <select id="locale">
          <option value="en-IN">English (India)</option>
          <option value="hi-IN">हिन्दी</option>
        </select>
      </label>
      <label class="step">
        <span class="step-label">Birth year</span>
        <input id="birth" type="number" min="1900" max="2020" placeholder="1992" required />
      </label>
    </div>
    <div class="step">
      <span class="step-label">Step 2 · Vault passcode</span>
      <input id="pass1" type="password" placeholder="passcode" autocomplete="new-password" required minlength="8" />
    </div>
    <div class="step">
      <span class="step-label">Step 3 · Confirm passcode</span>
      <input id="pass2" type="password" placeholder="confirm" autocomplete="new-password" required minlength="8" />
    </div>
    <button type="submit">Seal the vault</button>
    <div class="err" id="err" role="alert"></div>
  </form>

  <div class="hint">on-device · encrypted · nothing leaves this page</div>
  <div class="bot-rule">
    <span>MMXXVI</span>
    <span class="center">A WORK OF<span class="dot"></span>THE EMPIRE</span>
    <span class="crimson">Issue I</span>
  </div>
</section>
`;

export class WasiyatOnboarding extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const date = this.#shadow.getElementById('date');
    if (date) date.textContent = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    const err = this.#shadow.getElementById('err') as HTMLDivElement;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const name = (this.#shadow.getElementById('name') as HTMLInputElement).value.trim();
      const locale = (this.#shadow.getElementById('locale') as HTMLSelectElement).value as 'en-IN' | 'hi-IN';
      const birth = Number((this.#shadow.getElementById('birth') as HTMLInputElement).value);
      const pass1 = (this.#shadow.getElementById('pass1') as HTMLInputElement).value;
      const pass2 = (this.#shadow.getElementById('pass2') as HTMLInputElement).value;

      if (!name || !birth) {
        err.textContent = 'fill name and birth year';
        return;
      }
      if (pass1.length < 8) {
        err.textContent = 'passcode must be at least 8 characters';
        return;
      }
      if (pass1 !== pass2) {
        err.textContent = 'passcodes do not match';
        return;
      }
      if (birth < 1900 || birth > new Date().getFullYear() - 1) {
        err.textContent = 'birth year out of range';
        return;
      }

      try {
        const owner: VaultOwner = {
          display_name: name,
          locale,
          pronouns: 'they',
          birth_year: birth,
        };
        const vault = emptyVault(owner);
        const env = await encryptVault(pass1, vault, null);
        await saveEnvelope(env);
        await saveMeta({
          passkey_handle_b64: null,
          last_unlock_at: new Date().toISOString(),
          build: 'wasiyat-v0',
        });
        store.setVault(vault, pass1);
      } catch (e2) {
        err.textContent = 'could not seal vault. try again.';
      }
    });
  }
}

customElements.define('wasiyat-onboarding', WasiyatOnboarding);
